import { ProductName } from 'Constants/products.constants';
import { ChangeFormValues } from 'Forms/ChangeForm/changeForm.types';
import {
  isDowngradingCarCoverage,
  isDowngradingRentalCar,
  isUpgradingCarCoverage,
  isUpgradingRentalCar,
} from 'Forms/ChangeForm/products/car/changeCarForm.helpers';
import { RentalValue } from 'Forms/ChangeForm/products/house/changeHouseForm.types';
import { UsabilityCode } from 'Forms/ChangeForm/products/motorcycle/changeMotorcycleForm.constants';
import {
  isDowngradingMcCoverage,
  isMcUpgradingCoverage,
} from 'Forms/ChangeForm/products/motorcycle/helpers/changeMotorcycleForm.helpers';
import { ChangeTravelFormValues } from 'Forms/ChangeForm/products/travel/changeTravelForm.types';
import {
  addTravelFieldsToListOf30DaysChanges,
  isCoverageLengthUpgraded,
  isCoverageTypeChangedFromStandardToSuper,
  isTravelCoverageDowngraded,
  isTravelCoverageUpgraded,
} from 'Forms/ChangeForm/products/travel/helpers/changeTravelForm.businessRules.helpers';
import { isEqual, omit } from 'lodash';
import {
  isAutoProduct,
  isCabinProduct,
  isHouseProduct,
} from 'Utils/insuranceTypes';
import {
  isCabinForm,
  isContentsProduct,
  isCoverageInForm,
  isGarageInForm,
  isHouseForm,
  isMcLockerInForm,
  isRentalTypeInForm,
  isUsabilityCodeInForm,
  isUsedByYoungerDriverInForm,
  isYearlyKmNeedInForm,
} from 'Utils/products.utils';

export const isCoverageAmountIncreased = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (isContentsProduct(values) && isContentsProduct(initialValues)) {
    return values.coverageAmount > initialValues.coverageAmount;
  }

  return false;
};

export const isCoverageAmountDecreased = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (isContentsProduct(values) && isContentsProduct(initialValues)) {
    return values.coverageAmount < initialValues.coverageAmount;
  }

  return false;
};

export const isCoverageAmountChanged = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (isContentsProduct(values) && isContentsProduct(initialValues)) {
    return values.coverageAmount !== initialValues.coverageAmount;
  }

  return false;
};

export const isOwnContributionIncreased = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (!('ownContribution' in values) || !('ownContribution' in initialValues)) {
    return false;
  }

  return Number(values.ownContribution) > Number(initialValues.ownContribution);
};

export const isOwnContributionDecreased = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (!('ownContribution' in values) || !('ownContribution' in initialValues)) {
    return false;
  }

  return Number(values.ownContribution) < Number(initialValues.ownContribution);
};

export const isAutoProductAndOwnContributionDecreased = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  if (
    !isAutoProduct(productName) &&
    !isHouseProduct(productName) &&
    !isCabinProduct(productName)
  ) {
    return false;
  }

  return isOwnContributionDecreased(initialValues, values);
};

export const isRentalRemoved = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (isHouseForm(values) && isHouseForm(initialValues)) {
    return (
      values.rentalStatus === RentalValue.NO_RENTAL &&
      initialValues.rentalStatus !== RentalValue.NO_RENTAL
    );
  }

  if (isCabinForm(values) && isCabinForm(initialValues)) {
    return !values.hasRental && initialValues.hasRental;
  }
  return false;
};

export const isRentalAddedOrChanged = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (isHouseForm(values) && isHouseForm(initialValues)) {
    return (
      values.rentalStatus !== RentalValue.NO_RENTAL &&
      initialValues.rentalStatus !== values.rentalStatus
    );
  }

  if (isCabinForm(values) && isCabinForm(initialValues)) {
    return values.hasRental && !initialValues.hasRental;
  }
  return false;
};

export const isRentalChanged = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (isHouseForm(values) && isHouseForm(initialValues)) {
    return initialValues.rentalStatus !== values.rentalStatus;
  }

  if (isCabinForm(values) && isCabinForm(initialValues)) {
    return initialValues.hasRental !== values.hasRental;
  }
  return false;
};

export const isGarageChanged = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (!isGarageInForm(values) || !isGarageInForm(initialValues)) {
    return false;
  }
  return values.garage !== initialValues.garage;
};

export const isMcLockerChanged = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (!isMcLockerInForm(values) || !isMcLockerInForm(initialValues)) {
    return false;
  }
  return values.mcLocker !== initialValues.mcLocker;
};

export const isChangedToYoungerDriver = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (!isUsabilityCodeInForm(values) || !isUsabilityCodeInForm(initialValues)) {
    return false;
  }

  return (
    (isYoungerDriver(initialValues.usabilityCode) &&
      isAllAgesDriver(values.usabilityCode)) ||
    (isAdultDriver(initialValues.usabilityCode) &&
      isYoungerDriver(values.usabilityCode)) ||
    (isAdultDriver(initialValues.usabilityCode) &&
      isAllAgesDriver(values.usabilityCode))
  );
};

export const isChangedToOlderDriver = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (!isUsabilityCodeInForm(values) || !isUsabilityCodeInForm(initialValues)) {
    return false;
  }
  return (
    (isAllAgesDriver(initialValues.usabilityCode) &&
      isYoungerDriver(values.usabilityCode)) ||
    (isYoungerDriver(initialValues.usabilityCode) &&
      isAdultDriver(values.usabilityCode)) ||
    (isAllAgesDriver(initialValues.usabilityCode) &&
      isAdultDriver(values.usabilityCode))
  );
};

export const isChangedToUsedByYoungerDriver = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (
    !isUsedByYoungerDriverInForm(values) ||
    !isUsedByYoungerDriverInForm(initialValues)
  ) {
    return false;
  }

  return !initialValues.usedByYoungerDriver && values.usedByYoungerDriver;
};

export const isYearlyKmNeedChanged = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (!isYearlyKmNeedInForm(values) || !isYearlyKmNeedInForm(initialValues)) {
    return false;
  }

  return values.yearlyKmNeed !== initialValues.yearlyKmNeed;
};

export const isChangedToUsedByOlderDriver = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
): boolean => {
  if (
    !isUsedByYoungerDriverInForm(values) ||
    !isUsedByYoungerDriverInForm(initialValues)
  ) {
    return false;
  }

  return initialValues.usedByYoungerDriver && !values.usedByYoungerDriver;
};

const isAllAgesDriver = (usabilityCode: string): boolean =>
  usabilityCode === UsabilityCode.ALL_AGES;

const isYoungerDriver = (usabilityCode: string): boolean =>
  usabilityCode === UsabilityCode.YOUNG_DRIVER;

const isAdultDriver = (usabilityCode: string): boolean =>
  usabilityCode === UsabilityCode.ADULT_DRIVER;

export const isMcCoverageUpgraded = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  if (!isCoverageInForm(values) || !isCoverageInForm(initialValues)) {
    return false;
  }

  return (
    productName === ProductName.MOTORCYCLE &&
    isMcUpgradingCoverage(initialValues.coverage, values.coverage) &&
    initialValues.coverage !== values.coverage
  );
};

export const isMcCoverageDowngraded = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  if (!isCoverageInForm(values) || !isCoverageInForm(initialValues)) {
    return false;
  }
  return (
    productName === ProductName.MOTORCYCLE &&
    isDowngradingMcCoverage(initialValues.coverage, values.coverage) &&
    initialValues.coverage !== values.coverage
  );
};

export const isCarCoverageDowngraded = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  if (!isCoverageInForm(values) || !isCoverageInForm(initialValues)) {
    return false;
  }
  return (
    productName === ProductName.CAR &&
    isDowngradingCarCoverage(initialValues.coverage, values.coverage) &&
    initialValues.coverage !== values.coverage
  );
};

export const isCarCoverageUpgraded = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  if (!isCoverageInForm(values) || !isCoverageInForm(initialValues)) {
    return false;
  }
  return (
    productName === ProductName.CAR &&
    isUpgradingCarCoverage(initialValues.coverage, values.coverage) &&
    initialValues.coverage !== values.coverage
  );
};

export const isRentalCarUpgraded = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  if (!isRentalTypeInForm(values) || !isRentalTypeInForm(initialValues)) {
    return false;
  }
  return (
    productName === ProductName.CAR &&
    isUpgradingRentalCar(initialValues.rentalType, values.rentalType) &&
    initialValues.rentalType !== values.rentalType
  );
};

export const isRentalCarDowngraded = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  if (!isRentalTypeInForm(values) || !isRentalTypeInForm(initialValues)) {
    return false;
  }
  return (
    productName === ProductName.CAR &&
    isDowngradingRentalCar(initialValues.rentalType, values.rentalType) &&
    initialValues.rentalType !== values.rentalType
  );
};

export const hasChangesThatWillBeActiveIn30DaysOrRenewalDate = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  return (
    isAutoProductAndOwnContributionDecreased(
      initialValues,
      values,
      productName,
    ) ||
    isOwnContributionIncreased(initialValues, values) ||
    isCoverageAmountDecreased(initialValues, values) ||
    isRentalRemoved(initialValues, values) ||
    isGarageChanged(initialValues, values) ||
    isMcLockerChanged(initialValues, values) ||
    isChangedToOlderDriver(initialValues, values) ||
    isChangedToUsedByOlderDriver(initialValues, values) ||
    isMcCoverageDowngraded(initialValues, values, productName) ||
    isCarCoverageDowngraded(initialValues, values, productName) ||
    isRentalCarDowngraded(initialValues, values, productName) ||
    isTravelCoverageDowngraded(initialValues, values, productName)
  );
};

export const hasChangeThatWillBeActiveIn2DaysOrRenewalDate = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  return (
    isMcCoverageUpgraded(initialValues, values, productName) ||
    isRentalCarUpgraded(initialValues, values, productName) ||
    isTravelCoverageUpgraded(initialValues, values, productName)
  );
};

export const hasFutureChanges = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  return (
    hasChangesThatWillBeActiveIn30DaysOrRenewalDate(
      initialValues,
      values,
      productName,
    ) ||
    hasChangeThatWillBeActiveIn2DaysOrRenewalDate(
      initialValues,
      values,
      productName,
    )
  );
};

export const getTextForChangesThatWillBeActiveIn30DaysOrRenewalDate = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): string => {
  const listOf30DaysChanges = createListOf30DaysChanges(
    initialValues,
    values,
    productName,
  );

  return createStringFromListOfChanges(listOf30DaysChanges);
};

export const createStringFromListOfChanges = (
  listOfChanges: string[],
): string => {
  return listOfChanges.reduce((acc, field, index) => {
    if (index === 0) {
      return `${field} `;
    }
    if (index === listOfChanges.length - 1) {
      return `${acc}og ${field}`;
    }

    return `${acc}${field}, `;
  }, '');
};

const createListOf30DaysChanges = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): string[] => {
  const listOf30DaysChanges: string[] = [];

  if (isCoverageAmountDecreased(initialValues, values)) {
    listOf30DaysChanges.push('forsikringssum');
  }

  if (isRentalRemoved(initialValues, values)) {
    listOf30DaysChanges.push('utleie');
  }

  if (
    isOwnContributionIncreased(initialValues, values) ||
    isAutoProductAndOwnContributionDecreased(initialValues, values, productName)
  ) {
    listOf30DaysChanges.push('egenandel');
  }

  if (isGarageChanged(initialValues, values)) {
    listOf30DaysChanges.push('garasje');
  }

  if (isMcLockerChanged(initialValues, values)) {
    listOf30DaysChanges.push('sykkellås');
  }

  if (
    isChangedToOlderDriver(initialValues, values) ||
    isChangedToUsedByOlderDriver(initialValues, values)
  ) {
    listOf30DaysChanges.push('fører');
  }

  if (
    isMcCoverageDowngraded(initialValues, values, productName) ||
    isCarCoverageDowngraded(initialValues, values, productName)
  ) {
    listOf30DaysChanges.push('dekning');
  }

  if (isRentalCarDowngraded(initialValues, values, productName)) {
    listOf30DaysChanges.push('leiebil');
  }

  if (isTravelCoverageDowngraded(initialValues, values, productName)) {
    const travelFields = addTravelFieldsToListOf30DaysChanges(
      initialValues as ChangeTravelFormValues,
      values as ChangeTravelFormValues,
    );

    listOf30DaysChanges.push(...travelFields);
  }

  return listOf30DaysChanges;
};

export const getTextForChangesThatWillBeActiveInTwoDays = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): string => {
  const has30DaysChanges = hasChangesThatWillBeActiveIn30DaysOrRenewalDate(
    initialValues,
    values,
    productName,
  );
  const listOf2DaysChanges = createListOf2DaysChanges(
    initialValues,
    values,
    productName,
  );

  return listOf2DaysChanges.reduce((acc, field, index) => {
    if (has30DaysChanges && listOf2DaysChanges.length === 1) {
      return `Endring av ${field} `;
    }
    if (index === 0) {
      return `${field} `;
    }
    if (index === listOf2DaysChanges.length - 1) {
      return `${acc}og ${field}`;
    }

    return `${acc}${field}, `;
  }, '');
};

const createListOf2DaysChanges = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): string[] => {
  const listOf2DaysChanges: string[] = [];

  if (isMcCoverageUpgraded(initialValues, values, productName)) {
    listOf2DaysChanges.push('dekning');
  }

  if (isRentalCarUpgraded(initialValues, values, productName)) {
    listOf2DaysChanges.push('leiebil');
  }

  if (isTravelCoverageUpgraded(initialValues, values, productName)) {
    const {
      coverageType: initialCoverageType,
      coverageLength: initialCoverageLength,
    } = initialValues as ChangeTravelFormValues;
    const { coverageType, coverageLength } = values as ChangeTravelFormValues;

    if (
      isCoverageTypeChangedFromStandardToSuper(
        initialCoverageType,
        coverageType,
      )
    ) {
      listOf2DaysChanges.push('dekning');
    }

    if (isCoverageLengthUpgraded(initialCoverageLength, coverageLength)) {
      listOf2DaysChanges.push('antall dager');
    }
  }

  return listOf2DaysChanges;
};

export const hasChangesThatWillBeActiveFromToday = (
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  productName: ProductName,
): boolean => {
  return (
    !isEqual(
      omit(
        values,
        'ownContribution',
        'coverageAmount',
        'rentalStatus',
        'garage',
        'mcLocker',
        'usabilityCode',
        'coverageConfirmation',
        'coverage',
        'rentalType',
        'ownContributionCode',
        'usedByYoungerDriver',
      ),
      omit(
        initialValues,
        'ownContribution',
        'coverageAmount',
        'rentalStatus',
        'garage',
        'mcLocker',
        'usabilityCode',
        'coverageConfirmation',
        'coverage',
        'rentalType',
        'ownContributionCode',
        'usedByYoungerDriver',
      ),
    ) ||
    isCoverageAmountIncreased(initialValues, values) ||
    (isOwnContributionDecreased(initialValues, values) &&
      !isAutoProduct(productName) &&
      !isHouseProduct(productName) &&
      !isCabinProduct(productName)) ||
    isRentalAddedOrChanged(initialValues, values) ||
    isChangedToYoungerDriver(initialValues, values) ||
    isChangedToUsedByYoungerDriver(initialValues, values) ||
    isYearlyKmNeedChanged(initialValues, values)
  );
};
